
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  ChevronUp,
  Clock3,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Send,
} from "lucide-react";

const quickLinks = [
  { name: "About Us", path: "/about-us" },
  { name: "Admission Process", path: "/admission" },
  {
    name: "Mandatory Disclosure",
    path: "/mandatory-disclosure",
  },
  { name: "Facilities", path: "/facilities" },
  { name: "Photo Gallery", path: "/gallery" },
  { name: "Contact Us", path: "/enquiry" },
];

export default function FooterSection() {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="relative w-full overflow-hidden bg-[#250D10] text-white">

      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-24 -top-32 h-80 w-80 rounded-full border-[45px] border-white/[0.025]" />

        <div className="absolute -bottom-48 left-1/4 h-80 w-80 rounded-full bg-red-700/10 blur-3xl" />

        <div className="absolute right-1/4 top-0 h-48 w-48 rounded-full bg-amber-400/5 blur-3xl" />
      </div>

      {/* TOP ACCENT LINE */}
      <div className="relative h-[3px] w-full bg-gradient-to-r from-red-800 via-amber-400 to-red-800" />

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">

        {/* MAIN FOOTER CONTENT */}
        <div className="grid gap-9 py-10 sm:grid-cols-2 lg:grid-cols-[1.15fr_0.8fr_1.15fr_1fr] lg:gap-10 lg:py-12">

          {/* ABOUT SCHOOL */}
          <div>

            {/* LARGE LOGO ONLY */}
            <Link
              to="/"
              className="inline-block"
              aria-label="Go to Home"
            >
              <img
                src="/logo.png"
                alt="School Logo"
                width="210"
                height="210"
                loading="eager"
                className="h-auto w-[160px] object-contain sm:w-[190px] lg:w-[210px]"
              />
            </Link>

            <p className="mt-4 max-w-[330px] text-[13px] font-normal leading-[1.95] text-white/65">
              Pride of Purvanchal. An English medium
              school dedicated to quality education,
              character development and the pursuit
              of excellence since 1995.
            </p>

            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/10 px-3.5 py-2 text-[11px] font-normal text-amber-200">
              <GraduationCap size={15} />
              Learning • Values • Excellence
            </div>
          </div>

          {/* QUICK LINKS */}
          <div>
            <h3 className="font-serif text-[18px] font-medium text-white">
              Quick Links
            </h3>

            <div className="mt-3 h-[2px] w-11 rounded-full bg-amber-400" />

            <ul className="mt-5 space-y-3.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="group inline-flex items-center gap-2 text-[13px] font-normal text-white/65 transition-colors hover:text-amber-300"
                  >
                    <ChevronRight
                      size={15}
                      className="shrink-0 text-amber-400/70 transition-transform duration-200 group-hover:translate-x-1"
                    />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT INFORMATION */}
          <div>
            <h3 className="font-serif text-[18px] font-medium text-white">
              Contact Information
            </h3>

            <div className="mt-3 h-[2px] w-11 rounded-full bg-amber-400" />

            <div className="mt-5 space-y-4">

              {/* ADDRESS */}
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-amber-300">
                  <MapPin size={17} />
                </div>

                <div>
                  <p className="text-[11px] text-white/45">
                    School Address
                  </p>

                  <p className="mt-1 text-[13px] leading-6 text-white/70">
                    St. Xavier&apos;s Public School,
                    Bela, Cholapur, Varanasi,
                    Uttar Pradesh - 221101
                  </p>
                </div>
              </div>

              {/* PHONE */}
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-amber-300">
                  <Phone size={17} />
                </div>

                <div>
                  <p className="text-[11px] text-white/45">
                    Phone Numbers
                  </p>

                  <div className="mt-1 flex flex-col gap-0.5 text-[13px] text-white/70">
                    <a
                      href="tel:+918601017877"
                      className="transition hover:text-amber-300"
                    >
                      +91 86010 17877
                    </a>

                    <a
                      href="tel:+919450785369"
                      className="transition hover:text-amber-300"
                    >
                      +91 94507 85369
                    </a>
                  </div>
                </div>
              </div>

              {/* EMAIL */}
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-amber-300">
                  <Mail size={17} />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] text-white/45">
                    Email Address
                  </p>

                  <a
                    href="mailto:st.xavierbelavns@gmail.com"
                    className="mt-1 block break-all text-[13px] text-white/70 transition hover:text-amber-300"
                  >
                    st.xavierbelavns@gmail.com
                  </a>
                </div>
              </div>

              {/* OFFICE HOURS */}
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-amber-300">
                  <Clock3 size={17} />
                </div>

                <div>
                  <p className="text-[11px] text-white/45">
                    Office Hours
                  </p>

                  <p className="mt-1 text-[13px] text-white/70">
                    Mon - Sat: 9:00 AM - 4:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ADMISSION ENQUIRY */}
          <div>
            <h3 className="font-serif text-[18px] font-medium text-white">
              Admission Enquiry
            </h3>

            <div className="mt-3 h-[2px] w-11 rounded-full bg-amber-400" />

            <p className="mt-5 text-[13px] leading-7 text-white/65">
              Interested in learning more about
              St. Xavier&apos;s Public School?
              Get in touch with our team for
              admission-related information.
            </p>

            <div className="mt-5 rounded-2xl border border-amber-300/15 bg-white/[0.055] p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300">
                  <Send size={19} />
                </div>

                <div>
                  <p className="text-[13px] font-medium text-white">
                    Have a Question?
                  </p>

                  <p className="mt-1 text-[11px] text-white/50">
                    We are here to assist you.
                  </p>
                </div>
              </div>

              <Link
                to="/enquiry"
                className="group mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-700 to-red-600 px-4 py-3 text-[13px] font-medium text-white transition-all duration-300 hover:from-red-600 hover:to-red-500"
              >
                Send an Enquiry
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>

            <a
              href="tel:+918601017877"
              className="mt-4 inline-flex items-center gap-2 text-[12px] text-amber-200/90 transition hover:text-amber-300"
            >
              <Phone size={14} />
              Call for Assistance
              <ArrowRight size={13} />
            </a>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="border-t border-white/10 py-5">
          <div className="flex flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left">

            {/* COPYRIGHT */}
            <p className="text-[11px] font-normal leading-5 text-white/50">
              © {currentYear} St. Xavier&apos;s Public School.
              All rights reserved.
            </p>

            {/* LEGAL LINKS */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">

              <Link
                to="/privacy-policy"
                className="text-[11px] text-white/55 transition hover:text-amber-300"
              >
                Privacy Policy
              </Link>

              <Link
                to="/terms"
                className="text-[11px] text-white/55 transition hover:text-amber-300"
              >
                Terms of Use
              </Link>

              <Link
                to="/sitemap"
                className="text-[11px] text-white/55 transition hover:text-amber-300"
              >
                Sitemap
              </Link>
            </div>

            {/* BACK TO TOP */}
            <button
              type="button"
              onClick={scrollToTop}
              aria-label="Back to top"
              title="Back to Top"
              className="group inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-white/[0.07] px-3.5 py-2 text-[11px] text-amber-200 transition-all duration-300 hover:border-amber-300/50 hover:bg-white/[0.12]"
            >
              Back to Top
              <ChevronUp
                size={15}
                className="transition-transform duration-300 group-hover:-translate-y-0.5"
              />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
